import React, { useState, useEffect } from 'react';
import { Search, MapPin, Star, Phone, Globe, ChevronDown, Scale, Send, Building, CheckCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import PageFrame from '../components/PageFrame';
import SEO from '../components/SEO';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const LegalDirectory = () => {
  const [attorneys, setAttorneys] = useState([]);
  const [states, setStates] = useState([]);
  const [specialties, setSpecialties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedState, setSelectedState] = useState('');
  const [selectedSpecialty, setSelectedSpecialty] = useState('');
  const [showConsultationModal, setShowConsultationModal] = useState(false);
  const [showSubmitModal, setShowSubmitModal] = useState(false);
  const [selectedAttorney, setSelectedAttorney] = useState(null);
  const [consultationForm, setConsultationForm] = useState({
    name: '',
    email: '',
    phone: '',
    state: '',
    accident_date: '',
    description: ''
  });
  const [submitForm, setSubmitForm] = useState({
    name: '',
    firm_name: '',
    email: '',
    phone: '',
    website: '',
    states: [],
    specialties: [],
    description: '',
    years_experience: ''
  });

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    fetchAttorneys();
  }, [selectedState, selectedSpecialty]);

  const fetchData = async () => {
    try {
      const [statesRes, specialtiesRes] = await Promise.all([
        axios.get(`${API}/attorneys/states`),
        axios.get(`${API}/attorneys/specialties`)
      ]);
      setStates(statesRes.data.states);
      setSpecialties(specialtiesRes.data.specialties);
    } catch (error) {
      toast.error("Failed to load data");
    }
  };

  const fetchAttorneys = async () => {
    setLoading(true);
    try {
      let url = `${API}/attorneys/list`;
      const params = new URLSearchParams();
      if (selectedState && selectedState !== 'all') params.append('state', selectedState);
      if (selectedSpecialty && selectedSpecialty !== 'all') params.append('specialty', selectedSpecialty);
      if (params.toString()) url += `?${params.toString()}`;
      
      const response = await axios.get(url);
      setAttorneys(response.data.attorneys);
    } catch (error) {
      toast.error("Failed to load attorneys");
    } finally {
      setLoading(false);
    }
  };

  const handleConsultation = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/attorneys/consultation`, {
        ...consultationForm,
        attorney_id: selectedAttorney.id
      });
      toast.success("Consultation request submitted! The attorney will contact you soon.");
      setShowConsultationModal(false);
      setConsultationForm({ name: '', email: '', phone: '', state: '', accident_date: '', description: '' });
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to submit request");
    }
  };

  const handleSubmitListing = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/attorneys/submit`, {
        ...submitForm,
        years_experience: submitForm.years_experience ? parseInt(submitForm.years_experience) : null
      });
      toast.success("Your listing has been submitted for review!");
      setShowSubmitModal(false);
      setSubmitForm({ name: '', firm_name: '', email: '', phone: '', website: '', states: [], specialties: [], description: '', years_experience: '' });
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to submit listing");
    }
  };

  const openConsultationModal = (attorney) => {
    setSelectedAttorney(attorney);
    setShowConsultationModal(true);
  };

  return (
    <>
      <SEO 
        title="Find Truck Accident Attorneys Near You | Legal Directory"
        description="Connect with experienced truck accident attorneys in your state. Free consultation requests. Find the right legal representation for your case."
      />
      
      <PageFrame 
        title="Truck Accident Attorney Directory"
        subtitle="Find experienced attorneys who specialize in truck accident cases in your state."
      >
        <Tabs defaultValue="find" className="w-full">
          <TabsList className="grid w-full grid-cols-2 mb-8">
            <TabsTrigger value="find" className="data-[state=active]:bg-orange-500">Find an Attorney</TabsTrigger>
            <TabsTrigger value="list" className="data-[state=active]:bg-orange-500">List Your Practice</TabsTrigger>
          </TabsList>

          <TabsContent value="find">
            {/* Filters */}
            <div className="grid md:grid-cols-2 gap-4 mb-8">
              <Select value={selectedState} onValueChange={setSelectedState}>
                <SelectTrigger className="bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600">
                  <MapPin className="h-4 w-4 mr-2 text-orange-500" />
                  <SelectValue placeholder="Select State" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All States</SelectItem>
                  {states.map(state => (
                    <SelectItem key={state} value={state}>{state}</SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Select value={selectedSpecialty} onValueChange={setSelectedSpecialty}>
                <SelectTrigger className="bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600">
                  <Scale className="h-4 w-4 mr-2 text-orange-500" />
                  <SelectValue placeholder="Select Specialty" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Specialties</SelectItem>
                  {specialties.map(specialty => (
                    <SelectItem key={specialty} value={specialty}>{specialty}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Results */}
            {loading ? (
              <div className="flex justify-center py-12">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500"></div>
              </div>
            ) : attorneys.length > 0 ? (
              <div className="space-y-4">
                {attorneys.map(attorney => (
                  <Card key={attorney.id} className={`bg-white dark:bg-gray-800/50 border-gray-200 dark:border-gray-700 overflow-hidden ${attorney.featured ? 'ring-2 ring-orange-500' : ''}`}>
                    <CardContent className="p-6">
                      <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2">
                            {attorney.featured && (
                              <span className="text-xs font-bold text-orange-500 bg-orange-500/20 px-2 py-1 rounded">FEATURED</span>
                            )}
                            <h3 className="text-xl font-bold text-gray-900 dark:text-white">{attorney.name}</h3>
                          </div>
                          <p className="text-gray-600 dark:text-gray-400 flex items-center gap-1 mb-2">
                            <Building className="h-4 w-4" />
                            {attorney.firm_name}
                          </p>
                          <p className="text-gray-700 dark:text-gray-300 mb-3">{attorney.description}</p>
                          
                          <div className="flex flex-wrap gap-2 mb-3">
                            {attorney.states?.slice(0, 3).map(state => (
                              <span key={state} className="text-xs bg-blue-500/20 text-blue-400 px-2 py-1 rounded">
                                {state}
                              </span>
                            ))}
                            {attorney.states?.length > 3 && (
                              <span className="text-xs bg-gray-500/20 text-gray-400 px-2 py-1 rounded">
                                +{attorney.states.length - 3} more
                              </span>
                            )}
                          </div>

                          <div className="flex flex-wrap gap-2">
                            {attorney.specialties?.slice(0, 3).map(specialty => (
                              <span key={specialty} className="text-xs bg-orange-500/20 text-orange-400 px-2 py-1 rounded">
                                {specialty}
                              </span>
                            ))}
                          </div>
                        </div>

                        <div className="flex flex-col gap-2 md:items-end">
                          {attorney.years_experience && (
                            <p className="text-sm text-gray-500">{attorney.years_experience}+ years experience</p>
                          )}
                          {attorney.rating > 0 && (
                            <div className="flex items-center gap-1">
                              <Star className="h-4 w-4 text-yellow-500 fill-yellow-500" />
                              <span className="text-sm font-medium text-gray-700 dark:text-gray-300">{attorney.rating.toFixed(1)}</span>
                              <span className="text-xs text-gray-500">({attorney.review_count} reviews)</span>
                            </div>
                          )}
                          <div className="flex gap-2 mt-2">
                            {attorney.website && (
                              <Button 
                                variant="outline" 
                                size="sm"
                                onClick={() => window.open(attorney.website, '_blank')}
                                className="border-gray-300 dark:border-gray-600"
                              >
                                <Globe className="h-4 w-4" />
                              </Button>
                            )}
                            {attorney.phone && (
                              <Button 
                                variant="outline" 
                                size="sm"
                                onClick={() => window.open(`tel:${attorney.phone}`, '_self')}
                                className="border-gray-300 dark:border-gray-600"
                              >
                                <Phone className="h-4 w-4" />
                              </Button>
                            )}
                            <Button 
                              size="sm"
                              onClick={() => openConsultationModal(attorney)}
                              className="bg-orange-500 hover:bg-orange-600"
                            >
                              <Send className="h-4 w-4 mr-1" />
                              Request Consult
                            </Button>
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            ) : (
              <div className="text-center py-12">
                <Scale className="h-16 w-16 text-gray-400 mx-auto mb-4" />
                <h3 className="text-xl font-semibold text-gray-700 dark:text-gray-300 mb-2">No Attorneys Found</h3>
                <p className="text-gray-500 mb-6">We don't have any attorneys matching your criteria yet.</p>
                <Button onClick={() => setShowSubmitModal(true)} className="bg-orange-500 hover:bg-orange-600">
                  Be the First to List
                </Button>
              </div>
            )}
          </TabsContent>

          <TabsContent value="list">
            <Card className="bg-white dark:bg-gray-800/50 border-gray-200 dark:border-gray-700">
              <CardContent className="p-6">
                <div className="text-center mb-8">
                  <Scale className="h-12 w-12 text-orange-500 mx-auto mb-4" />
                  <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">List Your Practice</h3>
                  <p className="text-gray-600 dark:text-gray-400">
                    Reach thousands of truck accident victims looking for legal representation.
                  </p>
                </div>

                <div className="grid md:grid-cols-3 gap-6 mb-8">
                  <div className="text-center">
                    <CheckCircle className="h-8 w-8 text-green-500 mx-auto mb-2" />
                    <h4 className="font-semibold text-gray-800 dark:text-gray-200">Free Listing</h4>
                    <p className="text-sm text-gray-500">Basic listing at no cost</p>
                  </div>
                  <div className="text-center">
                    <CheckCircle className="h-8 w-8 text-green-500 mx-auto mb-2" />
                    <h4 className="font-semibold text-gray-800 dark:text-gray-200">Direct Leads</h4>
                    <p className="text-sm text-gray-500">Receive consultation requests</p>
                  </div>
                  <div className="text-center">
                    <CheckCircle className="h-8 w-8 text-green-500 mx-auto mb-2" />
                    <h4 className="font-semibold text-gray-800 dark:text-gray-200">Targeted Audience</h4>
                    <p className="text-sm text-gray-500">Reach people who need you</p>
                  </div>
                </div>

                <form onSubmit={handleSubmitListing} className="space-y-6">
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Full Name *</label>
                      <Input
                        value={submitForm.name}
                        onChange={(e) => setSubmitForm({...submitForm, name: e.target.value})}
                        required
                        className="bg-gray-100 dark:bg-gray-700 border-gray-300 dark:border-gray-600"
                      />
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Firm Name *</label>
                      <Input
                        value={submitForm.firm_name}
                        onChange={(e) => setSubmitForm({...submitForm, firm_name: e.target.value})}
                        required
                        className="bg-gray-100 dark:bg-gray-700 border-gray-300 dark:border-gray-600"
                      />
                    </div>
                  </div>

                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Email *</label>
                      <Input
                        type="email"
                        value={submitForm.email}
                        onChange={(e) => setSubmitForm({...submitForm, email: e.target.value})}
                        required
                        className="bg-gray-100 dark:bg-gray-700 border-gray-300 dark:border-gray-600"
                      />
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Phone *</label>
                      <Input
                        type="tel"
                        value={submitForm.phone}
                        onChange={(e) => setSubmitForm({...submitForm, phone: e.target.value})}
                        required
                        className="bg-gray-100 dark:bg-gray-700 border-gray-300 dark:border-gray-600"
                      />
                    </div>
                  </div>

                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Website</label>
                      <Input
                        type="url"
                        value={submitForm.website}
                        onChange={(e) => setSubmitForm({...submitForm, website: e.target.value})}
                        placeholder="https://"
                        className="bg-gray-100 dark:bg-gray-700 border-gray-300 dark:border-gray-600"
                      />
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Years of Experience</label>
                      <Input
                        type="number"
                        value={submitForm.years_experience}
                        onChange={(e) => setSubmitForm({...submitForm, years_experience: e.target.value})}
                        className="bg-gray-100 dark:bg-gray-700 border-gray-300 dark:border-gray-600"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="text-sm font-medium text-gray-700 dark:text-gray-300">States Covered * (Select multiple)</label>
                    <Select 
                      value={submitForm.states[0] || ''} 
                      onValueChange={(val) => {
                        if (!submitForm.states.includes(val)) {
                          setSubmitForm({...submitForm, states: [...submitForm.states, val]});
                        }
                      }}
                    >
                      <SelectTrigger className="bg-gray-100 dark:bg-gray-700 border-gray-300 dark:border-gray-600">
                        <SelectValue placeholder="Add states..." />
                      </SelectTrigger>
                      <SelectContent>
                        {states.map(state => (
                          <SelectItem key={state} value={state}>{state}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <div className="flex flex-wrap gap-2 mt-2">
                      {submitForm.states.map(state => (
                        <span 
                          key={state} 
                          className="text-xs bg-blue-500/20 text-blue-400 px-2 py-1 rounded cursor-pointer hover:bg-red-500/20 hover:text-red-400"
                          onClick={() => setSubmitForm({...submitForm, states: submitForm.states.filter(s => s !== state)})}
                        >
                          {state} ×
                        </span>
                      ))}
                    </div>
                  </div>

                  <div>
                    <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Specialties * (Select multiple)</label>
                    <Select 
                      value={submitForm.specialties[0] || ''} 
                      onValueChange={(val) => {
                        if (!submitForm.specialties.includes(val)) {
                          setSubmitForm({...submitForm, specialties: [...submitForm.specialties, val]});
                        }
                      }}
                    >
                      <SelectTrigger className="bg-gray-100 dark:bg-gray-700 border-gray-300 dark:border-gray-600">
                        <SelectValue placeholder="Add specialties..." />
                      </SelectTrigger>
                      <SelectContent>
                        {specialties.map(specialty => (
                          <SelectItem key={specialty} value={specialty}>{specialty}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <div className="flex flex-wrap gap-2 mt-2">
                      {submitForm.specialties.map(specialty => (
                        <span 
                          key={specialty} 
                          className="text-xs bg-orange-500/20 text-orange-400 px-2 py-1 rounded cursor-pointer hover:bg-red-500/20 hover:text-red-400"
                          onClick={() => setSubmitForm({...submitForm, specialties: submitForm.specialties.filter(s => s !== specialty)})}
                        >
                          {specialty} ×
                        </span>
                      ))}
                    </div>
                  </div>

                  <div>
                    <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Practice Description *</label>
                    <Textarea
                      value={submitForm.description}
                      onChange={(e) => setSubmitForm({...submitForm, description: e.target.value})}
                      required
                      rows={4}
                      placeholder="Tell potential clients about your experience with truck accident cases..."
                      className="bg-gray-100 dark:bg-gray-700 border-gray-300 dark:border-gray-600"
                    />
                  </div>

                  <Button 
                    type="submit" 
                    className="w-full bg-orange-500 hover:bg-orange-600"
                    disabled={submitForm.states.length === 0 || submitForm.specialties.length === 0}
                  >
                    Submit Listing for Review
                  </Button>
                </form>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Consultation Request Modal */}
        <Dialog open={showConsultationModal} onOpenChange={setShowConsultationModal}>
          <DialogContent className="bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700 max-w-md">
            <DialogHeader>
              <DialogTitle className="text-gray-900 dark:text-white">Request Consultation</DialogTitle>
              <DialogDescription className="text-gray-600 dark:text-gray-400">
                {selectedAttorney?.name} at {selectedAttorney?.firm_name}
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleConsultation} className="space-y-4 mt-4">
              <Input
                placeholder="Your Name *"
                value={consultationForm.name}
                onChange={(e) => setConsultationForm({...consultationForm, name: e.target.value})}
                required
                className="bg-gray-100 dark:bg-gray-700 border-gray-300 dark:border-gray-600"
              />
              <Input
                type="email"
                placeholder="Your Email *"
                value={consultationForm.email}
                onChange={(e) => setConsultationForm({...consultationForm, email: e.target.value})}
                required
                className="bg-gray-100 dark:bg-gray-700 border-gray-300 dark:border-gray-600"
              />
              <Input
                type="tel"
                placeholder="Your Phone *"
                value={consultationForm.phone}
                onChange={(e) => setConsultationForm({...consultationForm, phone: e.target.value})}
                required
                className="bg-gray-100 dark:bg-gray-700 border-gray-300 dark:border-gray-600"
              />
              <Input
                placeholder="Your State *"
                value={consultationForm.state}
                onChange={(e) => setConsultationForm({...consultationForm, state: e.target.value})}
                required
                className="bg-gray-100 dark:bg-gray-700 border-gray-300 dark:border-gray-600"
              />
              <Input
                type="date"
                placeholder="Accident Date"
                value={consultationForm.accident_date}
                onChange={(e) => setConsultationForm({...consultationForm, accident_date: e.target.value})}
                className="bg-gray-100 dark:bg-gray-700 border-gray-300 dark:border-gray-600"
              />
              <Textarea
                placeholder="Briefly describe your case *"
                value={consultationForm.description}
                onChange={(e) => setConsultationForm({...consultationForm, description: e.target.value})}
                required
                rows={3}
                className="bg-gray-100 dark:bg-gray-700 border-gray-300 dark:border-gray-600"
              />
              <Button type="submit" className="w-full bg-orange-500 hover:bg-orange-600">
                Send Request
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </PageFrame>
    </>
  );
};

export default LegalDirectory;
